import { DeleteOutlined, FieldTimeOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Checkbox, DatePicker, Form, List, Modal, Popconfirm, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import type { Membership, MembershipPeriodResult, Team } from '../../DTOS/ResourcePlanning';
import api from '../../services/resourcePlanning/ResourcePlanningService';
import { DATE, DISPLAY_DATE, memberState, membershipPeriod, OPEN_START, startText, today, useFeedback } from './shared';

const KEEP_ALLOCATIONS = 'Alocarile existente raman neschimbate. Se schimba doar echipa de baza si programul folosit pentru capacitate in zilele respective.';

/** Corrects one person's period in a team: start (or none), last day (or open-ended), or removes the record. */
export const MembershipPeriodModal: React.FC<{ member: Membership; team: Team; teams: Team[]; onClose: () => void }> = ({ member, team, teams, onClose }) => {
  const [form] = Form.useForm(); const feedback = useFeedback();
  const noStart: boolean = Form.useWatch('noStart', form); const noEnd: boolean = Form.useWatch('noEnd', form);
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onError: feedback.error });
  const others = teams.flatMap(item => item.memberships.filter(other => other.userId === member.userId && other.id !== member.id).map(other => ({ ...other, teamName: item.name })))
    .sort((a, b) => a.validFrom.localeCompare(b.validFrom));
  const save = async () => {
    try {
      const values = await form.validateFields();
      run.mutate(async () => {
        await api.editMembership({ id: member.id, expectedVersion: member.rowVersion, validFrom: values.noStart ? null : values.validFrom.format(DATE),
          validTo: values.noEnd ? null : values.lastDay.add(1, 'day').format(DATE) });
        feedback.success('Perioada din echipa a fost actualizata.'); onClose();
      });
    } catch (error) { feedback.error(error); }
  };
  const remove = () => run.mutate(async () => {
    await api.editMembership({ id: member.id, expectedVersion: member.rowVersion, validFrom: member.validFrom, validTo: member.validTo, delete: true });
    feedback.success('Apartenenta a fost stearsa.'); onClose();
  });
  return <Modal open title={<span><FieldTimeOutlined /> {member.userName} · {team.name}</span>} onCancel={onClose} destroyOnHidden
    footer={<Space>
      <Popconfirm title='Stergeti complet aceasta apartenenta?' description='Persoana nu va mai figura in echipa in aceasta perioada.' onConfirm={remove}>
        <Button danger icon={<DeleteOutlined />} disabled={run.isPending}>Sterge apartenenta</Button>
      </Popconfirm>
      <Button onClick={onClose}>Renunta</Button>
      <Button type='primary' loading={run.isPending} onClick={save}>Salveaza</Button>
    </Space>}>
    <Alert type='info' showIcon style={{ marginBottom: 16 }} message={KEEP_ALLOCATIONS} />
    <Form form={form} layout='vertical' initialValues={{
      noStart: member.validFrom <= OPEN_START, validFrom: member.validFrom > OPEN_START ? dayjs(member.validFrom) : dayjs(),
      noEnd: !member.validTo, lastDay: member.validTo ? dayjs(member.validTo).subtract(1, 'day') : undefined,
    }}>
      <Form.Item label='Membru din' style={{ marginBottom: 8 }}>
        <Space wrap>
          <Form.Item name='validFrom' noStyle rules={[{ required: !noStart, message: 'Selectati data de inceput.' }]}>
            <DatePicker format={DISPLAY_DATE} disabled={noStart} />
          </Form.Item>
          <Form.Item name='noStart' valuePropName='checked' noStyle><Checkbox>Fara data de inceput</Checkbox></Form.Item>
        </Space>
      </Form.Item>
      <Typography.Paragraph type='secondary' style={{ fontSize: 12 }}>
        Fara data de inceput, persoana apartine echipei din prima zi in care nu face parte din alta echipa.
      </Typography.Paragraph>
      <Form.Item label='Ultima zi in echipa'>
        <Space wrap>
          <Form.Item name='lastDay' noStyle dependencies={['validFrom', 'noStart', 'noEnd']} rules={[
            { required: !noEnd, message: 'Selectati ultima zi.' },
            ({ getFieldValue }) => ({ validator: (_, value) => noEnd || getFieldValue('noStart') || !value || !getFieldValue('validFrom') || !value.isBefore(getFieldValue('validFrom'), 'day')
              ? Promise.resolve() : Promise.reject(new Error('Ultima zi trebuie sa fie dupa data de inceput.')) }),
          ]}>
            <DatePicker format={DISPLAY_DATE} disabled={noEnd} />
          </Form.Item>
          <Form.Item name='noEnd' valuePropName='checked' noStyle><Checkbox>Nedeterminat</Checkbox></Form.Item>
        </Space>
      </Form.Item>
    </Form>
    {others.length > 0 && <List size='small' header={<Typography.Text strong>Alte apartenente ale persoanei</Typography.Text>} dataSource={others}
      renderItem={other => <List.Item>{other.teamName}: {membershipPeriod(other)}</List.Item>} />}
  </Modal>;
};

/** Sets the start date of every current and scheduled member, for planning the whole team over an earlier period. */
export const TeamStartModal: React.FC<{ team: Team; onClose: () => void }> = ({ team, onClose }) => {
  const [form] = Form.useForm(); const feedback = useFeedback(); const { modal } = App.useApp();
  const noStart: boolean = Form.useWatch('noStart', form);
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onError: feedback.error });
  // The server changes the earliest current or scheduled membership of each person; history stays.
  const members = [...team.memberships.filter(member => memberState(member) !== 'history')
    .reduce((all, member) => all.has(member.userId) && all.get(member.userId)!.validFrom <= member.validFrom ? all : all.set(member.userId, member), new Map<string, Membership>()).values()]
    .sort((a, b) => a.userName.localeCompare(b.userName));
  const showClipped = (result: MembershipPeriodResult) => result.clipped.length > 0 && modal.info({
    title: 'Unele persoane incep mai tarziu', width: 520,
    content: <Space direction='vertical'>
      <Typography.Text>Aceste persoane apartineau altei echipe inainte de data selectata. Apartenenta anterioara a fost pastrata.</Typography.Text>
      <List size='small' dataSource={result.clipped} renderItem={member => <List.Item>{member.userName}: din {startText(member.validFrom)}</List.Item>} />
    </Space>,
  });
  const save = async () => {
    try {
      const values = await form.validateFields();
      run.mutate(async () => {
        const result = await api.setTeamStart(team.id, values.noStart ? null : values.validFrom.format(DATE));
        feedback.success(`Data de inceput actualizata pentru ${result.updated} persoane.`); onClose(); showClipped(result);
      });
    } catch (error) { feedback.error(error); }
  };
  return <Modal open title={<span><FieldTimeOutlined /> Data de inceput · {team.name}</span>} onCancel={onClose} onOk={save}
    okText='Aplica intregii echipe' cancelText='Renunta' confirmLoading={run.isPending} okButtonProps={{ disabled: !members.length }} destroyOnHidden>
    <Alert type='info' showIcon style={{ marginBottom: 16 }} message={KEEP_ALLOCATIONS}
      description='Pentru o singura persoana, folositi Modifica perioada din dreptul ei.' />
    <Form form={form} layout='vertical' initialValues={{ noStart: false, validFrom: dayjs(today()) }}>
      <Form.Item label='Toti membrii incepand cu'>
        <Space wrap>
          <Form.Item name='validFrom' noStyle rules={[{ required: !noStart, message: 'Selectati data de inceput.' }]}>
            <DatePicker format={DISPLAY_DATE} disabled={noStart} />
          </Form.Item>
          <Form.Item name='noStart' valuePropName='checked' noStyle><Checkbox>Fara data de inceput</Checkbox></Form.Item>
        </Space>
      </Form.Item>
    </Form>
    <List size='small' header={<Typography.Text strong>{members.length} membri curenti si programati</Typography.Text>} dataSource={members}
      locale={{ emptyText: 'Niciun membru curent sau programat.' }}
      renderItem={member => <List.Item>{member.userName}<Typography.Text type='secondary'>{membershipPeriod(member)}</Typography.Text></List.Item>} />
  </Modal>;
};
